import React from 'react';
import { View } from '@adobe/react-spectrum';
import { getProductIcons } from '../../../../utils/categoryIconUtils';
import styles from './MultipleProductIcons.module.scss';

interface MultipleProductIconsProps {
  products: string[];
  maxVisible?: number;
  iconSize?: string;
  onIconClick?: (product: string) => void;
}

export const MultipleProductIcons: React.FC<MultipleProductIconsProps> = ({
  products = [],
  maxVisible = 3,
  iconSize = '32px',
  onIconClick
}) => {
  // Show max 3 icons then +count
  const maxIconsInContainer = 3;
  const visibleProducts = products.slice(0, maxIconsInContainer);
  const remainingCount = Math.max(0, products.length - maxIconsInContainer);

  // Get product icons using the utility function
  const productIconPaths = getProductIcons(visibleProducts);

  const handleImageError = (event: any) => {
    event.target.src = '/images/apps_icons/AdobeIcon.svg';
  };

  if (products.length === 0) {
    // Show default Adobe icon when no products selected
    return (
      <div className={styles.singleIconContainer}>
        <img
          src="/images/apps_icons/AdobeIcon.svg"
          alt="No products selected"
          width={iconSize}
          height={iconSize}
          onError={handleImageError}
          style={
            {
              display: 'block',
              imageRendering: '-webkit-optimize-contrast',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale'
            } as React.CSSProperties
          }
        />
      </div>
    );
  }

  if (products.length === 1 && productIconPaths.length > 0) {
    // Show single icon when only one product selected
    return (
      <div
        className={styles.singleIconContainer}
        onClick={() => onIconClick?.(products[0])}
        role={onIconClick ? 'button' : undefined}
        tabIndex={onIconClick ? 0 : undefined}
      >
        <img
          src={productIconPaths[0]}
          alt={products[0]}
          width={iconSize}
          height={iconSize}
          onError={handleImageError}
          style={
            {
              display: 'block',
              imageRendering: '-webkit-optimize-contrast',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale'
            } as React.CSSProperties
          }
        />
      </div>
    );
  }

  // Show multiple icons when more than one product selected
  return (
    <View UNSAFE_className={styles.multipleIconsContainer}>
      <div className={styles.iconStack}>
        {productIconPaths.map((iconPath, index) => (
          <div
            key={`${products[index]}-${index}`}
            className={styles.stackedIcon}
            onClick={() => onIconClick?.(products[index])}
            role={onIconClick ? 'button' : undefined}
            tabIndex={onIconClick ? 0 : undefined}
          >
            <img
              src={iconPath}
              alt={products[index]}
              width={iconSize}
              height={iconSize}
              onError={handleImageError}
              className={styles.productIcon}
              style={
                {
                  display: 'block',
                  imageRendering: '-webkit-optimize-contrast',
                  WebkitFontSmoothing: 'antialiased',
                  MozOsxFontSmoothing: 'grayscale'
                } as React.CSSProperties
              }
            />
          </div>
        ))}
        {remainingCount > 0 && (
          <div className={styles.remainingCount}>
            <span className={styles.countText}>+{remainingCount}</span>
          </div>
        )}
      </div>
    </View>
  );
};
